import { KeyboardEventHandler, ReactNode, Ref } from "react"

import { SearchInput } from "../../../../components/base/input/SearchInput/SearchInput"
import { classNames } from "../../../../lib/classNames"
import {
  BLOCK_TABLE_CONTROL_BASE_CLASS,
  BLOCK_TABLE_CONTROL_EXTENDED_CLASS,
} from "../table.styles"
import { TableActionButton, TableActionButtonProps } from "../TableActionButton"

interface BlockTableControlProps {
  searchInput?: ReactNode
  actions?: ReactNode
  sort?: ReactNode
  filters?: ReactNode
  filterActions?: ReactNode
  activeCount?: ReactNode
  hideShowColumns?: ReactNode
  customControls?: ReactNode
  extendedStyle?: boolean
  containerClassName?: string
}

export function BlockTableControl({
  searchInput,
  actions,
  sort,
  filters,
  filterActions,
  activeCount,
  hideShowColumns,
  customControls,
  extendedStyle = false,
  containerClassName,
}: BlockTableControlProps) {
  const hasControls =
    !!customControls ||
    !!sort ||
    !!filters ||
    !!filterActions ||
    !!hideShowColumns ||
    !!activeCount

  return (
    <div
      className={classNames(
        BLOCK_TABLE_CONTROL_BASE_CLASS,
        extendedStyle && BLOCK_TABLE_CONTROL_EXTENDED_CLASS,
        containerClassName
      )}
    >
      {searchInput && (
        <>
          <div className="flex flex-row flex-wrap items-center justify-between gap-4">
            {searchInput}
            {actions}
          </div>
          {hasControls && (
            <BlockTableSortAndFilterControls
              activeCount={activeCount}
              customControls={customControls}
              sort={sort}
              filters={filters}
              filterActions={filterActions}
              hideShowColumns={hideShowColumns}
            />
          )}
        </>
      )}
      {!searchInput && (
        <div className="flex flex-row flex-wrap items-center justify-between gap-4">
          <BlockTableSortAndFilterControls
            activeCount={activeCount}
            customControls={customControls}
            sort={sort}
            filters={filters}
            filterActions={filterActions}
            hideShowColumns={hideShowColumns}
          />
          {actions}
        </div>
      )}
    </div>
  )
}

interface BlockTableControlSearchProps {
  searchQuery: string
  onSearchQueryChange: (query: string) => void
  searchPlaceholder?: string
  autoFocus?: boolean
  className?: string
  containerClassName?: string
  ref?: Ref<HTMLInputElement>
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>
}
export function BlockTableControlSearch({
  searchQuery,
  onSearchQueryChange,
  searchPlaceholder = "Search",
  autoFocus,
  className = "w-85",
  containerClassName,
  ref,
  onKeyDown,
}: BlockTableControlSearchProps) {
  return (
    <SearchInput
      value={searchQuery}
      onChange={(e) => onSearchQueryChange(e.target.value)}
      placeholder={searchPlaceholder}
      autoFocus={autoFocus}
      hideKeyboardTile
      inputSize="sm"
      className={className}
      containerClassName={containerClassName}
      ref={ref}
      onKeyDown={onKeyDown}
    />
  )
}

interface BlockTableControlActionsProps {
  actions: TableActionButtonProps[]
}

export function BlockTableControlActions({
  actions,
}: BlockTableControlActionsProps) {
  return (
    <div className="flex flex-row justify-end gap-2">
      {actions.map((action, index) => {
        const { key, ...actionProps } = action
        return (
          <TableActionButton
            key={key || action.label || `action-${index}`}
            {...actionProps}
          />
        )
      })}
    </div>
  )
}

interface BlockTableSortAndFilterControlsProps {
  sort: ReactNode
  filters: ReactNode
  filterActions?: ReactNode
  hideShowColumns?: ReactNode
  customControls?: ReactNode
  activeCount?: ReactNode
}

export function BlockTableSortAndFilterControls({
  sort,
  filters,
  filterActions,
  hideShowColumns,
  customControls,
  activeCount,
}: BlockTableSortAndFilterControlsProps) {
  const controls = (
    <div className="flex flex-row flex-wrap items-center gap-2">
      {customControls}
      {sort}
      {hideShowColumns}
      {filters}
    </div>
  )
  return activeCount || filterActions ? (
    <div className="flex flex-row flex-wrap items-center justify-between gap-4">
      {controls}
      <div className="flex flex-row items-center gap-4">
        {activeCount}
        {filterActions}
      </div>
    </div>
  ) : (
    <div className="flex flex-row flex-wrap items-center gap-2">{controls}</div>
  )
}
